import {
  Badge,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { GroupType } from "../../types/group.type";
import { COLORS, HEADER_HEIGHT } from "../../utils/ui.constants";
import Options from "./components/Options";
import { useEffect, useMemo, useState } from "react";
import { LuFilter } from "react-icons/lu";
import { asyncGetList, asyncPostData } from "../../utils/httpRequests";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../contexts/alert/AlertProvider";
import Tablepagination from "../../components/common/tableapp/TablePagination";
import ModalResult from "./components/ModalResult";
import Summary from "./components/Summary";
import TableApp from "../../components/common/tableapp/TableApp";
import { TableColumn } from "react-data-table-component";
import { useBackdropContext } from "../../contexts/backdrop/BackdropProvider";

interface IGroupBase {
  group: GroupType;
  fixedHeaderScrollHeight?: string;
  wrapperHeight?: string;
}

const heights = {
  header: HEADER_HEIGHT,
  py: "32px",
  topList: "50px",
  pagination: "42px",
  sumary: "30px",
};
const widthes = {
  filter: "350px",
};

function GroupBase({
  group,
  fixedHeaderScrollHeight,
  wrapperHeight,
}: IGroupBase) {
  const { showAlert } = useAlertContext();
  const { setOpenBackdrop } = useBackdropContext();
  const token = useSelector((state: any) => state.auth.token);
  const [load, setLoad] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandFilter, setExpandFilter] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [toggleCleared, setToggleCleared] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [condition, setCondition] = useState<
    { [key: string]: any } | undefined
  >({});
  const [conditionResult, setConditionResult] = useState<
    { [key: string]: any } | undefined
  >(undefined);
  const [sumary, setSumary] = useState<{
    min: number;
    max: number;
    average: number;
    median: number;
    nearest: number;
  }>({
    min: 0,
    max: 0,
    average: 0,
    median: 0,
    nearest: 0,
  });
  const [paginationOption, setPaginationOption] = useState<{
    page: number;
    limit: number;
    totalRows: number;
  }>({
    limit: 20,
    page: 1,
    totalRows: 0,
  });

  // row per page change
  const handleRowPerPageChange = (value: number) => {
    setPaginationOption({ ...paginationOption, page: 1, limit: value });
  };

  // selected row change
  const handleSelectedRowsChange = (rows: any[]) => {
    setSelectedRows(rows);
  };

  const handleExclude = async () => {
    if (!selectedRows || selectedRows?.length === 0) {
      return;
    }
    setOpenBackdrop?.(true);
    const ids = (selectedRows || []).map((item) => item._id);
    const resp = await asyncPostData({
      apiCode: group.apiCodeList,
      token,
      data: { key: "", name: "" },
      condition: { q: { _id: { $in: ids } } },
      extraUrl: "/updateskeysearch",
    });
    if (!resp || resp?.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error ||
          resp?.data?.message ||
          "Lỗi khi loại bỏ các kết quả",
      });
      setOpenBackdrop?.(false);
      return;
    } else {
      setLoad(load + 1);
      setOpenBackdrop?.(false);
    }
  };

  const getListData = async () => {
    try {
      if (loading) return;
      setLoading(true);
      let query: any = { ...group.condition, ...condition };

      const respSummary = await asyncGetList({
        apiCode: group.apiCode,
        withIdApp: true,
        applyToken: true,
        token,
        condition: {
          page: paginationOption.page,
          limit: paginationOption.limit,
          q: query,
        },
        stringifyData: true,
      });
      const resp = await asyncGetList({
        apiCode: group.apiCodeList,
        token,
        condition: {
          page: paginationOption.page,
          limit: paginationOption.limit,
          q: query,
        },
      });
      const respCount = await asyncGetList({
        apiCode: group.apiCodeList,
        token,
        condition: { count: 1, q: query },
      });

      if (
        respSummary.status === 200 &&
        resp?.status === 200 &&
        respCount?.status === 200
      ) {
        const result = respSummary?.data || [];
        const min = result.shift()?.dongia || 0;
        const max = result.shift()?.dongia || 0;
        const nearest = result.shift()?.dongia || 0;
        const average = result.shift()?.dongia || 0;
        const median = result.shift()?.dongia || 0;
        setSumary({ min, max, average, median, nearest });
        setData(resp?.data || []);
        setPaginationOption({
          ...paginationOption,
          totalRows: respCount.data.rows_number || 0,
        });
        setLoading(false);
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.data?.message || error?.data?.error || "Lỗi khi tải dữ liệu",
      });
    } finally {
    }
  };

  const findNearest = async (dongia: number) => {
    const resp = await asyncGetList({
      apiCode: group.apiCodeList,
      token,
      condition: {
        page: 1,
        limit: 10,
        sort: { ngayquyetdinh: "-1" },
        q: { ...condition, dongia: dongia },
      },
    });
    if (resp && resp?.status === 200) {
      if (resp.data?.length > 0) {
        const ngayGanNhat = resp?.data?.[0];
        setConditionResult({
          ...condition,
          dongia: dongia,
          ngayquyetdinh: ngayGanNhat.ngayquyetdinh,
        });
      }
    } else {
      showAlert({
        type: "error",
        message: resp?.data?.error || resp?.data?.message || "Có lỗi xảy ra",
      });
    }
  };

  const columns: TableColumn<any>[] = useMemo(() => {
    return (group.columns || []).map((col: TableColumn<any>) => {
      if (!col.cell) {
        return {
          ...col,
          cell: (row, index) => {
            return (
              <Tooltip
                placement="right"
                arrow
                TransitionComponent={Zoom}
                title={`${
                  col.format ? col.format(row, index) : col.selector?.(row)
                }`}
              >
                <div data-tag="allowRowEvents" className="two-lines-ellipsis">
                  {col.format ? col.format(row, index) : col.selector?.(row)}
                </div>
              </Tooltip>
            );
          },
        };
      }
      return col;
    });
  }, [group.columns]);

  useEffect(() => {
    if (group.apiCode) {
      setToggleCleared(!toggleCleared);
      getListData();
      setSelectedRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationOption.limit, paginationOption.page, load]);

  useEffect(() => {
    if (group.apiCode) {
      setCondition([]);
      setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
      setLoad(load + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.apiCode]);

  useEffect(() => {
    setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
    setLoad(load + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  return (
    <>
      {!!conditionResult && (
        <ModalResult
          open={!!conditionResult}
          onClose={() => setConditionResult(undefined)}
          condition={conditionResult}
        />
      )}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderRadius: "10px",
        }}
      >
        {/* Top */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            height: heights.topList,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{ fontSize: "20px", lineHeight: "20px", fontWeight: 600 }}
          >
            {group?.title || "Danh sách"}
          </Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            {group.showExclued && (
              <Badge badgeContent={selectedRows?.length || 0} color="error">
                <Button
                  onClick={() => {
                    showAlert({
                      type: "info",
                      message: `Bạn có chắc muốn loại bỏ ${selectedRows?.length} dòng đã chọn khỏi báo cáo`,
                      onConfirm: handleExclude,
                    });
                  }}
                  disabled={!selectedRows || selectedRows?.length === 0}
                  size="small"
                  variant="contained"
                  sx={{
                    boxShadow: "none",
                    color: "common.white",
                    textTransform: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  Loại bỏ
                </Button>
              </Badge>
            )}
            <Options
              query={{ ...group.condition, ...condition }}
              group={group}
              totalRows={paginationOption.totalRows}
              setLoad={setLoad}
            />
            {group?.allowFilter && (
              <IconButton
                onClick={() => setExpandFilter(!expandFilter)}
                sx={{
                  borderRadius: "10px",
                  color: "common.white",
                  backgroundColor: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.main",
                  },
                }}
              >
                <LuFilter size={16} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {/* Top end */}
        {/* Sumary */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{
            height: heights.sumary,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Summary text="Trung bình:" value={sumary.average} />
          <Summary
            text="Trung vị:"
            value={sumary.median}
            onClick={() => {
              findNearest(sumary.median);
            }}
          />
          <Summary
            text="Cao nhất:"
            value={sumary.max}
            sx={{ color: COLORS.max }}
            onClick={() => {
              findNearest(sumary.max);
            }}
          />
          <Summary
            text="Thấp nhất:"
            value={sumary.min}
            sx={{ color: COLORS.min }}
            onClick={() => {
              findNearest(sumary.min);
            }}
          />
          <Summary
            text="Gần nhất:"
            value={sumary.nearest}
            sx={{ color: COLORS.near }}
            onClick={() => {
              findNearest(sumary.nearest);
            }}
          />
        </Stack>
        {/* Sumary end */}
        {/* Table */}
        <Box
          id="table-filter-layout"
          sx={{
            width: "100%",
            height:
              wrapperHeight ||
              `calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} - ${heights.sumary} - ${heights.pagination})`,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: `calc(100%${expandFilter ? ` - ${widthes.filter}` : ""})`,
              height: "100%",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
            }}
          >
            <TableApp
              toggleCleared={toggleCleared}
              selectableRowsNoSelectAll
              rowPerPage={paginationOption.limit}
              columns={columns}
              data={data}
              selectableRows
              fixedHeader
              progressPending={loading}
              onSelectedRowsChange={handleSelectedRowsChange}
              fixedHeaderScrollHeight={
                fixedHeaderScrollHeight ||
                `calc(100vh - ${HEADER_HEIGHT} - ${heights.py} - ${heights.topList} - ${heights.sumary} - ${heights.pagination})`
              }
            />
          </Box>
          {/* Filter */}
          <Box
            sx={{
              height: "100%",
              width: expandFilter ? widthes.filter : "0px",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
              borderLeft: "2px solid",
              borderColor: expandFilter ? "primary.main" : "transparent",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              {group?.allowFilter && group.Filter && (
                <group.Filter setCondition={setCondition} />
              )}
            </Box>
            {/* <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{
                height: "45px",
                borderTop: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Button
                onClick={() => {
                  setFilter({});
                  setPaginationOption({
                    ...paginationOption,
                    page: 1,
                    totalRows: 0,
                  });
                  // setLoad(load + 1);
                }}
                variant="contained"
                size="small"
                color="secondary"
                sx={{
                  textTransform: "none",
                  color: "common.white",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Làm mới
              </Button>
              <Button
                onClick={() => setExpandFilter(false)}
                variant="contained"
                size="small"
                color="error"
                sx={{
                  textTransform: "none",
                  color: "common.white",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Đóng
              </Button>
            </Stack> */}
          </Box>
        </Box>
        {/* Table end */}
        {/* Pagination */}
        <Box
          sx={{
            mt: "auto",
            borderTop: "1px dashed",
            borderColor: "divider",
            height: heights.pagination,
          }}
        >
          {paginationOption.totalRows > 0 && (
            <Tablepagination
              currentPage={paginationOption.page}
              onChangePage={(value) =>
                setPaginationOption({ ...paginationOption, page: value })
              }
              rowsPerPage={paginationOption.limit}
              rowCount={paginationOption.totalRows}
              onChangeRowsPerPage={handleRowPerPageChange}
              paginationRowsPerPageOptions={[20, 50, 100, 500]}
            />
          )}
        </Box>
        {/* Pagination end */}
      </Box>
    </>
  );
}

export default GroupBase;
